import { h } from 'preact'
import { useState, useRef, useEffect } from 'preact/compat'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useController } from 'react-hook-form'
import { getCountryName } from '../../../helpers/billingAddress'
import { useTranslations } from '../../../hooks/useTranslations'
import { countries } from '../../../assets/countries'
import { SelectIcon } from '../../../assets/dynamicIcons/SelectIcon'
import styles from './AddressSelector.module.scss'

const setAsFirstCountry = firstCountry => {
  // sort counties
  const sortedCountries = countries.sort((a, b) => a.NAME.localeCompare(b.NAME))

  // set firstCountry as first country of the list
  return sortedCountries.sort((a, b) => {
    if (a.CODE === firstCountry) {
      return -1
    }

    if (b.CODE === firstCountry) {
      return 1
    }

    return 0
  })
}

function AddressSelector({ setValue, errors, control, preselectedCountry }) {
  const l10n = useTranslations()
  const [filteredCountries, setFilteredCountries] = useState(
    setAsFirstCountry(preselectedCountry),
  )

  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)
  const hasError = Boolean(errors['country'])

  const [activeIndex, setActiveIndex] = useState(0)
  const [currentValue, setCurrentValue] = useState(
    setAsFirstCountry(preselectedCountry)[0].NAME,
  )
  const activeElement = useRef(null)

  useEffect(() => {
    activeElement?.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    })
  }, [activeIndex])

  const inputClasses = classNames([
    {
      error: hasError,
    },
    'input',
    'input-text',
    'payment-form__field',
  ])

  const isDropDownOpened = useRef(false)

  const handleSelect = (value, onChange, onBlur) => {
    // setTimeout needed to trigger this logic after onMouseDown will be triggered
    setTimeout(() => {
      setCurrentValue(l10n.t(value.NAME))
      onChange(l10n.t(value.NAME))
      setIsDropDownOpen(false)
      onBlur()
      isDropDownOpened.current = false
    }, 1)
  }

  const { field } = useController({
    name: 'country',
    control,
    rules: { required: true },
    defaultValue: getCountryName(preselectedCountry, l10n),
  })

  const countriesFilter = inputValue =>
    countries.filter(country =>
      l10n.t(country.NAME).toLowerCase().startsWith(inputValue.toLowerCase()),
    )

  function handleInput(countryInput) {
    setIsDropDownOpen(true)
    isDropDownOpened.current = true

    setActiveIndex(0)

    if (preselectedCountry && countryInput === '') {
      setFilteredCountries(setAsFirstCountry(preselectedCountry))
    } else if (!preselectedCountry && countryInput === '') {
      setFilteredCountries(countries)
    } else {
      setFilteredCountries(countriesFilter(countryInput))
    }
  }

  const scrollClick = useRef(false)

  function handleOnBlur() {
    const inputValue = inputRef.current.value
    const isMatchedCountry = countries.find(
      country => l10n.t(country.NAME) === inputValue,
    )

    if (!isMatchedCountry) {
      setValue('country', '')
      setFilteredCountries(countries)
    }

    // Since onBlur doesn't provide event.key and event.keyCode we need to understand somehow which the key was pressed
    // hence isDropDownOpened ref was created to sync onKeyDown (take key code) and onBlur event
    setTimeout(() => {
      if (isDropDownOpened.current && !scrollClick.current) {
        field.onBlur()
        setIsDropDownOpen(false)
        isDropDownOpened.current = false
      }

      scrollClick.current = false
    }, 1)
  }

  function handleOnKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()

      if (isDropDownOpened.current) {
        setActiveIndex(
          activeIndex + 1 === filteredCountries.length ? 0 : activeIndex + 1,
        )
      }

      setIsDropDownOpen(true)
      isDropDownOpened.current = true
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()

      if (isDropDownOpened.current) {
        setActiveIndex(
          activeIndex <= 0 ? filteredCountries.length - 1 : activeIndex - 1,
        )
      }

      setIsDropDownOpen(true)
      isDropDownOpened.current = true
    } else if (e.key === 'Enter') {
      e.preventDefault()

      if (isDropDownOpened.current) {
        const selected = filteredCountries.findIndex(
          o => l10n.t(o.NAME) === filteredCountries[activeIndex].NAME,
        )

        setActiveIndex(selected < 0 ? 0 : selected)
        handleSelect(
          setAsFirstCountry(preselectedCountry)[activeIndex],
          field.onChange,
          field.onBlur,
        )
      }

      setIsDropDownOpen(true)
      isDropDownOpened.current = true
    } else if (e.key === 'Escape') {
      e.preventDefault()

      handleOnBlur()
    }
  }

  function handleOnClick() {
    setIsDropDownOpen(true)
    isDropDownOpened.current = true
  }

  const handleBackgroundClick = () => {
    if (isDropDownOpened.current && isDropDownOpen && !scrollClick.current) {
      handleOnBlur()
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleBackgroundClick)

    return () => document.removeEventListener('click', handleBackgroundClick)
  }, [isDropDownOpened.current, isDropDownOpen, scrollClick.current])

  return (
    <div className={styles.selectorContainer} data-testid="address-selector">
      <label className={inputClasses}>
        <span className="label">{l10n.t('Country of Residence')}</span>
        <div className={`group ${styles.group}`}>
          <input
            aria-expanded={isDropDownOpen}
            aria-haspopup="listbox"
            role="combobox"
            type="text"
            onInput={e => {
              field.onChange(e.target.value)
              handleInput(e.target.value)
            }}
            onBlur={handleOnBlur}
            onClick={handleOnClick}
            onKeyDown={handleOnKeyDown}
            name={field.name}
            value={field.value}
            ref={e => {
              field.ref(e)
              inputRef.current = e
            }}
            data-testid="address-selector-input"
          />
          <SelectIcon />
        </div>
        {hasError && (
          <p className="message" data-testid="error-message">
            <span className="icon xsmall"></span>
            {l10n.t(
              'Please select your country of residence from the dropdown selection',
            )}
          </p>
        )}
      </label>

      {isDropDownOpen && (
        <ul
          ref={dropdownRef}
          className={`group payment-form__field ${styles.optionsContainer}`}
          data-testid="address-selector-dropdown"
          role="listbox"
          tabIndex="-1"
          onMouseDown={e => {
            // allowing to use scrollbar
            if (e.offsetX + 20 > e.target.clientWidth) {
              scrollClick.current = true

              setIsDropDownOpen(true)
              isDropDownOpened.current = true

              const selected = filteredCountries.findIndex(
                o => l10n.t(o.NAME) === currentValue,
              )
              setActiveIndex(selected < 0 ? 0 : selected)
            }
          }}
        >
          {filteredCountries.length ? (
            filteredCountries.map((country, i) => (
              <li
                onMouseDown={() => {
                  const selected = filteredCountries.findIndex(
                    o => l10n.t(o.NAME) === country.NAME,
                  )

                  setActiveIndex(selected)
                  handleSelect(country, field.onChange, field.onBlur)
                }}
                className={styles.option}
                key={country.ISO}
                data-testid={`address-selector-option-${country.ISO}`}
                tabIndex="0"
                role="option"
                aria-selected={i === activeIndex}
                ref={i === activeIndex ? activeElement : null}
              >
                {l10n.t(country.NAME)}
              </li>
            ))
          ) : (
            <li
              className={styles.option}
              data-testid="address-selector-option-no-data"
              tabIndex="-1"
            >
              {l10n.t('No results')}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

AddressSelector.propTypes = {
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  preselectedCountry: PropTypes.string,
}

export default AddressSelector
